import React, { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Navn skal udfyldes';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer skal udfyldes';
    } else if (!/^[+0-9\s-]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Indtast venligst et gyldigt telefonnummer';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-mail skal udfyldes';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Indtast venligst en gyldig e-mailadresse';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Beskrivelse skal udfyldes';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: ''
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus('error');
      setErrorMessage(err.message || 'Der skete en netværksfejl. Kontroller din internetforbindelse og prøv igen.');
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-8 text-center shadow-sm">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white mb-4">
          <i className="fa-solid fa-check text-2xl" />
        </div>
        <h3 className="font-display text-xl font-bold text-emerald-900 mb-2">Tak for din henvendelse!</h3>
        <p className="text-sm text-emerald-700 leading-relaxed">
          Vi har modtaget din besked og vender tilbage til dig hurtigst muligt – normalt inden for 24 timer på hverdage.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="mt-6 rounded-full bg-emerald-600 px-6 py-2 text-sm font-bold text-white transition-colors hover:bg-emerald-700"
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-100 bg-white p-8 shadow-xl">
      <h3 className="font-display text-xl font-bold text-brand-dark mb-2">Få et uforpligtende tilbud</h3>
      <p className="text-sm text-slate-500 mb-6">Udfyld formularen nedenfor, så kontakter vi dig hurtigst muligt for en uforpligtende snak om din opgave.</p>

      {status === 'error' && (
        <div className="mb-6 rounded-xl bg-rose-50 border border-rose-100 p-4 flex items-start gap-3 text-rose-800 text-sm">
          <i className="fa-regular fa-circle-exclamation text-rose-500 text-lg mt-0.5" />
          <p>{errorMessage}</p>
        </div>
      )}

      {/* Honeypot Field - Absolutely hidden from humans */}
      <div className="sr-only absolute" aria-hidden="true">
        <label htmlFor="website_url">Honeypot - lad dette felt være tomt</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>

      <div className="flex flex-col gap-5">
        {/* Navn */}
        <div>
          <label htmlFor="name" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
            Navn <span className="text-rose-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
              <i className="fa-regular fa-user" />
            </span>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={status === 'submitting'}
              className={`w-full rounded-xl border bg-slate-50 py-3.5 pl-11 pr-4 text-sm text-slate-900 outline-none transition-all focus:border-brand-accent focus:bg-white focus:ring-2 focus:ring-brand-accent-light ${
                errors.name ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : 'border-slate-200'
              }`}
              placeholder="Dit fulde navn"
            />
          </div>
          {errors.name && <p className="mt-1.5 text-xs font-semibold text-rose-500">{errors.name}</p>}
        </div>

        {/* Telefonnummer & Email */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="phone" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              Telefonnummer <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
                <i className="fa-regular fa-phone" />
              </span>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={status === 'submitting'}
                className={`w-full rounded-xl border bg-slate-50 py-3.5 pl-11 pr-4 text-sm text-slate-900 outline-none transition-all focus:border-brand-accent focus:bg-white focus:ring-2 focus:ring-brand-accent-light whitespace-nowrap ${
                  errors.phone ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : 'border-slate-200'
                }`}
                placeholder="Tlf. nummer"
              />
            </div>
            {errors.phone && <p className="mt-1.5 text-xs font-semibold text-rose-500">{errors.phone}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              E-mail <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
                <i className="fa-regular fa-envelope" />
              </span>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={status === 'submitting'}
                className={`w-full rounded-xl border bg-slate-50 py-3.5 pl-11 pr-4 text-sm text-slate-900 outline-none transition-all focus:border-brand-accent focus:bg-white focus:ring-2 focus:ring-brand-accent-light whitespace-nowrap ${
                  errors.email ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : 'border-slate-200'
                }`}
                placeholder="Din e-mailadresse"
              />
            </div>
            {errors.email && <p className="mt-1.5 text-xs font-semibold text-rose-500">{errors.email}</p>}
          </div>
        </div>

        {/* Beskrivelse */}
        <div>
          <label htmlFor="message" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
            Beskrivelse af din opgave <span className="text-rose-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            rows="4"
            value={formData.message}
            onChange={handleChange}
            disabled={status === 'submitting'}
            className={`w-full rounded-xl border bg-slate-50 p-4 text-sm text-slate-900 outline-none transition-all focus:border-brand-accent focus:bg-white focus:ring-2 focus:ring-brand-accent-light ${
              errors.message ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : 'border-slate-200'
            }`}
            placeholder="Fortæl os kort om din opgave, f.eks. montering af alarm, portservice eller adgangskontrol..."
          />
          {errors.message && <p className="mt-1.5 text-xs font-semibold text-rose-500">{errors.message}</p>}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="mt-2 w-full rounded-xl bg-brand-accent py-4 text-sm font-bold text-white shadow-md transition-all hover:bg-brand-accent-hover disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
        >
          {status === 'submitting' ? (
            <>
              <i className="fa-solid fa-spinner animate-spin" />
              <span>Sender din besked...</span>
            </>
          ) : (
            <>
              <i className="fa-regular fa-paper-plane" />
              <span>Send besked</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}
